import type { SerializedWorkerError } from './envelope';

/** The serialize side caps at this depth too; inbound chains from divergent peers are not trusted to. */
const MAX_CAUSE_DEPTH = 8;

/**
 * Rebuild an Error carrying the remote name/message/stack/cause chain, so a worker-side
 * rejection reads like the worker threw it — because it did.
 */
export const deserializeWorkerError = (
    serialized: SerializedWorkerError,
    depth = 0,
): Error & { readonly code?: string } => {
    const error = new Error(
        serialized.message,
        serialized.cause !== undefined && depth < MAX_CAUSE_DEPTH
            ? { cause: deserializeWorkerError(serialized.cause, depth + 1) }
            : undefined,
    );
    error.name = serialized.name;
    if (serialized.stack !== undefined) {
        error.stack = serialized.stack;
    }
    return Object.assign(error, serialized.code !== undefined ? { code: serialized.code } : {});
};

/** A `handleRequest` rejection, rehydrated on the client. The remote error rides as `cause`. */
export class WorkerRequestError extends Error {
    readonly remote: SerializedWorkerError;

    constructor(remote: SerializedWorkerError) {
        super(`Worker request failed: ${remote.name}: ${remote.message}`, {
            cause: deserializeWorkerError(remote),
        });
        this.name = 'WorkerRequestError';
        this.remote = remote;
    }
}

/**
 * The worker never became usable: script/spawn failure, a `boot-error` frame, or the spawn
 * deadline elapsing. Rejects `ready` and every held request.
 */
export class WorkerSpawnError extends Error {
    constructor(message: string, options?: { cause?: unknown }) {
        super(message, options);
        this.name = 'WorkerSpawnError';
    }
}

/** The worker was usable and stopped being so: missed-pong death detection or unexpected pipe closure. */
export class WorkerDiedError extends Error {
    constructor(message: string, options?: { cause?: unknown }) {
        super(message, options);
        this.name = 'WorkerDiedError';
    }
}
